import React from "react";
import { BUSINESS, COMPLIANCE_LINE_SHORT } from "@/lib/constants";
import Seo from "@/components/Seo";
import CtaStrip from "@/components/CtaStrip";

const Section = ({ title, children, testId }) => (
    <section data-testid={testId} className="mt-12">
        <h2 className="font-serif text-2xl sm:text-3xl text-[#1A362B] tracking-tight font-medium">{title}</h2>
        <div className="divider-thin my-5" />
        <div className="space-y-4 text-[#1C1C1C]/80 leading-relaxed">{children}</div>
    </section>
);

export default function CreditGuidePage() {
    return (
        <div data-testid="page-credit-guide" className="pt-20">
            <Seo
                title="Credit Guide & Privacy | Finance Wealth Mortgage Broker"
                description="Finance Wealth Credit Guide and Privacy Disclosure — licensing, fees, commissions, conflicts and how we handle your personal information."
            />
            <section className="bg-[#F3F1EA]">
                <div className="container-prose py-20 md:py-28">
                    <div className="max-w-3xl">
                        <div className="overline">Legal &amp; Compliance</div>
                        <h1 className="mt-4 font-serif text-5xl sm:text-6xl tracking-tight leading-[1.02] text-[#1A362B] font-medium">
                            Credit Guide &amp; Privacy.
                        </h1>
                        <div className="divider-thin my-8" />
                        <p className="text-lg text-[#1C1C1C]/75 leading-relaxed font-light">
                            This Credit Guide provides important information about {BUSINESS.brand} and our credit
                            services, in accordance with the <em>National Consumer Credit Protection Act 2009 (Cth)</em>.
                        </p>
                    </div>
                </div>
            </section>

            <section className="bg-background">
                <div className="container-prose py-16 max-w-4xl">
                    <Section title="1. About us" testId="credit-section-1">
                        <p>
                            <strong>{BUSINESS.companyName}</strong> (ABN {BUSINESS.companyAbn}) trades as
                            {" "}<strong>{BUSINESS.brand}</strong>, a boutique Australia-wide online mortgage brokerage providing credit
                            assistance to consumers across Australia.
                        </p>
                        <ul className="list-disc pl-6 space-y-1.5">
                            <li><strong>Office:</strong> {BUSINESS.addressLine1}, {BUSINESS.addressLine2}, {BUSINESS.addressLine3}</li>
                            <li><strong>Phone:</strong> {BUSINESS.phone}</li>
                            <li><strong>Email:</strong> {BUSINESS.email}</li>
                        </ul>
                    </Section>

                    <Section title="2. Our licensing &amp; authorisations" testId="credit-section-2">
                        <p>{COMPLIANCE_LINE_SHORT}</p>
                        <div className="bg-[#F3F1EA] border border-border rounded-sm p-6 mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                            <div>
                                <div className="text-xs tracking-widest uppercase text-[#C69C6D] font-semibold">Australian Credit Licence</div>
                                <div className="mt-1 text-[#1A362B] font-medium">{BUSINESS.aclHolder}</div>
                                <div className="text-[#1C1C1C]/70">ACL No. {BUSINESS.aclNumber}</div>
                            </div>
                            <div>
                                <div className="text-xs tracking-widest uppercase text-[#C69C6D] font-semibold">Credit Representative</div>
                                <div className="mt-1 text-[#1A362B] font-medium">{BUSINESS.creditRepName}</div>
                                <div className="text-[#1C1C1C]/70">CR No. {BUSINESS.creditRepNumber}</div>
                            </div>
                            <div>
                                <div className="text-xs tracking-widest uppercase text-[#C69C6D] font-semibold">Corporate Credit Representative</div>
                                <div className="mt-1 text-[#1A362B] font-medium">{BUSINESS.corporateCreditRepName}</div>
                                <div className="text-[#1C1C1C]/70">CCR No. {BUSINESS.corporateCreditRepNumber}</div>
                            </div>
                            <div>
                                <div className="text-xs tracking-widest uppercase text-[#C69C6D] font-semibold">Company</div>
                                <div className="mt-1 text-[#1A362B] font-medium">{BUSINESS.companyName}</div>
                                <div className="text-[#1C1C1C]/70">ABN {BUSINESS.companyAbn}</div>
                            </div>
                        </div>
                    </Section>

                    <Section title="3. The credit services we provide" testId="credit-section-3">
                        <p>
                            We provide credit assistance by helping you to apply for, or vary, a credit contract — including
                            home loans, investment loans, refinances, commercial loans, personal loans and asset finance.
                            We have access to a panel of over 60 lenders.
                        </p>
                        <p>
                            Before recommending a loan, we conduct a Preliminary Assessment to determine whether the loan
                            is <em>not unsuitable</em> for you, based on your stated requirements, objectives and a
                            reasonable inquiry into your financial situation. You can request a free copy of this
                            assessment within 7 years of the date we provide credit assistance.
                        </p>
                    </Section>

                    <Section title="4. Disclaimer" testId="credit-section-4">
                        <div className="bg-[#1A362B] text-white p-6 rounded-sm">
                            <p data-testid="credit-disclaimer-statement" className="text-white/90 leading-relaxed italic">
                                {BUSINESS.disclaimer}
                            </p>
                        </div>
                        <p className="mt-4">
                            Indicative rates, calculators and product information presented on this website are general in
                            nature, do not take into account your personal circumstances, and should not be relied upon as
                            advice. Actual rates, fees and approval are subject to lender assessment.
                        </p>
                    </Section>

                    <Section title="5. Fees, commissions &amp; conflicts" testId="credit-section-5">
                        <p>
                            We typically do not charge you a fee for our credit assistance — lenders pay us a commission
                            once your loan settles. The amount varies by lender and product, and consists of an upfront
                            commission (paid at settlement) and a trail commission (paid monthly over the life of the
                            loan). Full commission details are provided in your Credit Proposal Disclosure Document.
                        </p>
                        <p>
                            We will always recommend a loan based on what is right for you — not on the commission it
                            pays us. If a fee is to be charged in your specific circumstances, it will be disclosed and
                            agreed in writing before any work is performed.
                        </p>
                    </Section>

                    <Section title="6. Privacy" testId="credit-section-6">
                        <p>
                            {BUSINESS.brand} is committed to protecting your privacy. We collect, use and disclose your
                            personal and credit-related information in accordance with the <em>Privacy Act 1988 (Cth)</em>,
                            the Australian Privacy Principles and the Credit Reporting Code.
                        </p>
                        <p><strong>What we collect:</strong> identification, contact, employment, income, expenses, asset
                            and liability information, and credit-related information from credit reporting bodies.
                        </p>
                        <p><strong>Why we collect it:</strong> to assess your needs, prepare a Preliminary Assessment,
                            submit a credit application to a lender on your behalf, and to meet our legal obligations.
                        </p>
                        <p><strong>Who we share it with:</strong> lenders and lenders' mortgage insurers; aggregators
                            (Brass Aggregation Pty Ltd); referrers (where applicable); credit reporting bodies; service
                            providers (e.g. CRM, document storage); and government bodies where required by law.
                        </p>
                        <p><strong>Your rights:</strong> you may request access to or correction of the personal
                            information we hold about you, withdraw consent, or make a privacy complaint at any time by
                            contacting us at {BUSINESS.email}.
                        </p>
                    </Section>

                    <Section title="7. Complaints &amp; external dispute resolution" testId="credit-section-7">
                        <p>
                            If you have a complaint, please contact us first at {BUSINESS.email} or {BUSINESS.phone} —
                            we will acknowledge it promptly and investigate. If you are not satisfied with the outcome,
                            you may refer the matter to the Australian Financial Complaints Authority (AFCA):
                        </p>
                        <ul className="list-disc pl-6 space-y-1.5">
                            <li>Website: <a className="underline hover:text-[#1A362B]" href="https://www.afca.org.au" target="_blank" rel="noopener noreferrer">www.afca.org.au</a></li>
                            <li>Phone: 1800 931 678</li>
                            <li>Email: info@afca.org.au</li>
                            <li>Post: GPO Box 3, Melbourne VIC 3001</li>
                        </ul>
                    </Section>

                    <p className="mt-12 text-xs text-[#1C1C1C]/50">
                        This Credit Guide is current as at {new Date().toLocaleDateString("en-AU", { month: "long", year: "numeric" })} and may be updated from time to time.
                    </p>
                </div>
            </section>

            <CtaStrip />
        </div>
    );
}
